'use client';

import { useState } from 'react';

import { useBoolean } from 'usehooks-ts';

import {
  Badge,
  Button,
  LocaleLink,
  TypographyH6,
  TypographyMuted,
} from '@/components';
import { useProductValue } from '@/context';
import { numberFormat, PATHNAME, randomId } from '@/utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    CATEGORY: { BASE_URL: CATEGORY_BASE_URL },
  },
  QUERY_PARAM: {
    SYMBOL: { PLUS: PLUS_SYMBOL },
  },
} = PATHNAME;

const ProductAttributesBase = () => {
  const { baseAttributes = [] } = useProductValue();

  const [baseAttributesOpen, setBaseAttributesOpen] = useState(
    Array.from({ length: 5 }, () => false),
  );

  const baseAttributesLimit = 3;
  const validBaseAttributes = baseAttributes.filter(
    ({ selectedValues }) => selectedValues.length > 0,
  );
  const isExistedAttributes = validBaseAttributes.length > 0;

  if (isExistedAttributes) {
    return (
      <div className='space-y-2'>
        <TypographyH6 className='font-bold'>ویژگی‌ها:</TypographyH6>

        {validBaseAttributes
          .slice(0, 5)
          .map(({ id, name, selectedValues }, i) => {
            const totalValues = selectedValues.length;

            return (
              <div key={id} className='flex flex-wrap items-center gap-1'>
                <TypographyMuted>{name}:</TypographyMuted>

                {selectedValues
                  .slice(
                    0,
                    !baseAttributesOpen[i] ? baseAttributesLimit : undefined,
                  )
                  .map(value => {
                    const id = randomId();

                    return (
                      <Badge key={id} variant='outline'>
                        {value}
                      </Badge>
                    );
                  })}

                {totalValues > baseAttributesLimit ? (
                  <Badge
                    variant='outline'
                    className='cursor-pointer'
                    onClick={() =>
                      setBaseAttributesOpen(prev => prev.with(i, !prev[i]))
                    }
                  >
                    {baseAttributesOpen[i]
                      ? 'نمایش کمتر'
                      : numberFormat(
                          totalValues - baseAttributesLimit,
                          {},
                          PLUS_SYMBOL,
                        )}
                  </Badge>
                ) : null}
              </div>
            );
          })}
      </div>
    );
  }
};

const ProductAttributesCategories = () => {
  const { otherCategories = [] } = useProductValue();

  const { value: showCategories, setValue: setShowCategories } = useBoolean();

  const categoriesLimit = 3;
  const totalCategories = otherCategories.length;
  const isExistedCategories = totalCategories > 0;

  if (isExistedCategories) {
    return (
      <div className='space-y-2'>
        <TypographyH6 className='font-bold'>دسته‌بندی‌ها:</TypographyH6>

        <div className='inline-flex flex-wrap gap-1.5'>
          {otherCategories
            .slice(0, !showCategories ? categoriesLimit : undefined)
            .map(({ id, slug, title }) => (
              <Button
                asChild
                key={id}
                variant='muted'
                size='sm'
                className='font-semibold'
              >
                <LocaleLink
                  href={`${CATEGORY_BASE_URL}${slug}${DEFAULT_SLUG_SEPARATOR}`}
                >
                  {title}
                </LocaleLink>
              </Button>
            ))}

          {totalCategories > categoriesLimit ? (
            <Button
              variant='muted'
              size='sm'
              className='font-semibold text-foreground'
              onClick={() => setShowCategories(prev => !prev)}
            >
              {showCategories
                ? 'نمایش کمتر'
                : numberFormat(
                    totalCategories - categoriesLimit,
                    {},
                    PLUS_SYMBOL,
                  )}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
};

const ProductAttributes = () => {
  return (
    <div className='flex shrink-0 flex-col gap-4'>
      <ProductAttributesBase />
      <ProductAttributesCategories />
    </div>
  );
};

export { ProductAttributes };
